import React from 'react';
import { Building2, Phone, Smartphone, Mail, MapPin, Hash, Briefcase } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Form fields condivisi per il blocco "Dati commerciali" / Sede Store dell'agente.
 *
 * Props:
 *   value: oggetto store_info
 *   onChange: (newValue) => void
 *   disabled: bool
 *   compact: bool  -> layout una colonna
 */
export const AgentStoreInfoFields = ({ value, onChange, disabled = false, compact = false }) => {
  const v = value || {};

  const set = (key) => (e) => {
    const next = { ...v, [key]: e.target.value };
    onChange?.(next);
  };

  const colCls = compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2';

  return (
    <div className="space-y-4" data-testid="agent-store-info-fields">
      {/* --- Anagrafica consulente --- */}
      <div className={`grid ${colCls} gap-3`}>
        <Field label="Ruolo / Mansione" icon={Briefcase}>
          <Input
            value={v.job_title || ''}
            onChange={set('job_title')}
            disabled={disabled}
            placeholder="Es: Consulente Energia, Responsabile Store"
            data-testid="store-info-job-title"
          />
        </Field>
        <Field label="Cellulare diretto" icon={Smartphone}>
          <Input
            value={v.mobile_phone || ''}
            onChange={set('mobile_phone')}
            disabled={disabled}
            placeholder="Es: +39 333 1234567"
            data-testid="store-info-mobile-phone"
          />
        </Field>
        <Field label="Email pubblica (cliente)" icon={Mail} fullSpan={!compact}>
          <Input
            type="email"
            value={v.email || ''}
            onChange={set('email')}
            disabled={disabled}
            placeholder="Es: gianluigi.accattato@mainsolution.eu"
            data-testid="store-info-email"
          />
        </Field>
      </div>

      {/* --- Sede store --- */}
      <div className="pt-3 border-t border-slate-200">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#0B2A4A] mb-3 flex items-center gap-1.5">
          <Building2 className="h-3.5 w-3.5" /> Sede Store di riferimento
        </p>
        <div className={`grid ${colCls} gap-3`}>
          <Field label="Nome punto vendita" icon={Building2} fullSpan={!compact}>
            <Input
              value={v.store_name || ''}
              onChange={set('store_name')}
              disabled={disabled}
              placeholder="Es: Main Solution Rimini Centro"
              data-testid="store-info-store-name"
            />
          </Field>
          <Field label="Codice store" icon={Hash}>
            <Input
              value={v.store_code || ''}
              onChange={set('store_code')}
              disabled={disabled}
              placeholder="Es: RMN-01"
              data-testid="store-info-store-code"
            />
          </Field>
          <Field label="Telefono store (fisso)" icon={Phone}>
            <Input
              value={v.phone || ''}
              onChange={set('phone')}
              disabled={disabled}
              placeholder="Es: 0541 1234567"
              data-testid="store-info-phone"
            />
          </Field>
          <Field label="Indirizzo" icon={MapPin} fullSpan={!compact}>
            <Input
              value={v.address || ''}
              onChange={set('address')}
              disabled={disabled}
              placeholder="Es: Via Roma 12"
              data-testid="store-info-address"
            />
          </Field>
          <Field label="Città">
            <Input
              value={v.city || ''}
              onChange={set('city')}
              disabled={disabled}
              placeholder="Es: Rimini"
              data-testid="store-info-city"
            />
          </Field>
          <div className={compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-2 gap-3'}>
            <Field label="CAP">
              <Input
                value={v.cap || ''}
                onChange={set('cap')}
                disabled={disabled}
                placeholder="47921"
                maxLength={5}
                data-testid="store-info-cap"
              />
            </Field>
            <Field label="Prov.">
              <Input
                value={v.province || ''}
                onChange={set('province')}
                disabled={disabled}
                placeholder="RN"
                maxLength={2}
                data-testid="store-info-province"
                style={{ textTransform: 'uppercase' }}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
};

// Inline helper component (kept inside file as it is trivial)
const Field = ({ label, icon: Icon, children, fullSpan }) => (
  <div className={`space-y-1.5 ${fullSpan ? 'sm:col-span-2' : ''}`}>
    <Label className="text-[11px] font-semibold uppercase tracking-wide text-[#0B2A4A] flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3 text-slate-400" />} {label}
    </Label>
    {children}
  </div>
);
